import React, { useState } from "react";
import {
  User,
  MapPin,
  Phone,
  CreditCard,
  Briefcase,
  DollarSign,
  GraduationCap,
  Shield,
  Users,
  FileText,
  Eye,
  EyeOff,
  Loader,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useEffect } from "react";

const EmployeeProfile = () => {
  const { user } = useAuth();

  const [showSalaryDetails, setShowSalaryDetails] = useState(false);
  function toTitleCase(str) {
    if (!str) return "";
    return str
      .toString()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  const [employee, setEmployee] = useState(null);

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/${user.eid}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        const data = await response.json();
        if (response.ok) {
          if (Object.keys(data).length > 0) {
            setEmployee(data);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchEmployee();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const InfoCard = ({ title, icon: Icon, children, className = "" }) => (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
    >
      <div className="flex items-center mb-4">
        <Icon className="h-5 w-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, className = "" }) => (
    <div
      className={`flex justify-between py-2 border-b border-gray-100 last:border-b-0 ${className}`}
    >
      <span className="text-sm font-medium text-gray-600">{label}:</span>
      <span className="text-sm text-gray-900">{value || "Not provided"}</span>
    </div>
  );

  if (!employee)
    return (
      <>
        <Loader className="mx-auto" />
      </>
    );
  // if not employee return a descriptive message that say fill required details
  if (!employee.personal_information)
    return (
      <>
        <article className="max-w-xl mx-auto p-6 bg-white rounded-2xl shadow-md border">
          <header className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold">
                {employee.employee_name}
              </h3>
              <p className="text-sm text-gray-500">{employee.eid}</p>
            </div>
          </header>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="space-y-1">
              <dt className="text-gray-500">First name</dt>
              <dd className="font-medium">{employee.first_name || "-"}</dd>
            </div>

            <div className="space-y-1">
              <dt className="text-gray-500">Middle name</dt>
              <dd className="font-medium">{employee.middle_name || "-"}</dd>
            </div>

            <div className="space-y-1">
              <dt className="text-gray-500">Last name</dt>
              <dd className="font-medium">{employee.last_name || "-"}</dd>
            </div>

            <div className="space-y-1">
              <dt className="text-gray-500">Mobile</dt>
              <dd className="font-medium">{employee.mobile_number || "-"}</dd>
            </div>

            <div className="space-y-1 sm:col-span-2">
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium break-all">
                {employee.email_address || "-"}
              </dd>
            </div>

            <div className="space-y-1 sm:col-span-2">
              <dt className="text-gray-500">Remark</dt>
              <dd className="font-medium">{employee.remark || "-"}</dd>
            </div>
          </dl>
        </article>
      </>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  {toTitleCase(employee.personal_information.employee_name)}
                </h1>
                <p className="text-gray-600">
                  {toTitleCase(employee.employment_details.designation)}
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-gray-500">
                    Employee ID: {employee.eid}
                  </span>
                  {employee.isVerified && (
                    <div className="ml-2 flex items-center">
                      <Shield className="h-4 w-4 text-green-500 mr-1" />
                      <span className="text-xs text-green-600 font-medium">
                        Verified
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Member since</p>
              <p className="font-medium">
                {formatDate(employee.employment_details.date_of_joining)}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <InfoCard title="Personal Information" icon={User}>
            <div className="space-y-1">
              <InfoRow
                label="Full Name"
                value={toTitleCase(employee.personal_information.employee_name)}
              />
              <InfoRow
                label="Father's Name"
                value={toTitleCase(employee.personal_information.father_name)}
              />
              <InfoRow
                label="Mother's Name"
                value={toTitleCase(employee.personal_information.mother_name)}
              />
              <InfoRow
                label="Date of Birth"
                value={formatDate(employee.personal_information.date_of_birth)}
              />
              <InfoRow
                label="Place of Birth"
                value={toTitleCase(
                  employee.personal_information.place_of_birth
                )}
              />
              <InfoRow
                label="Gender"
                value={toTitleCase(employee.personal_information.gender)}
              />
              <InfoRow
                label="Nationality"
                value={toTitleCase(employee.personal_information.nationality)}
              />
              <InfoRow
                label="Religion"
                value={toTitleCase(employee.personal_information.religion)}
              />
              <InfoRow
                label="Marital Status"
                value={toTitleCase(
                  employee.personal_information.marital_status
                )}
              />
              <InfoRow
                label="Blood Group"
                value={toTitleCase(employee.personal_information.blood_group)}
              />
              <InfoRow
                label="Languages Known"
                value={toTitleCase(
                  employee.personal_information.languages_known.join(", ")
                )}
              />
            </div>
          </InfoCard>

          {/* Address Information */}
          <InfoCard title="Address Information" icon={MapPin}>
            <div className="space-y-1">
              <InfoRow
                label="Village/Area"
                value={toTitleCase(
                  employee.address_information.permanent_address.village
                )}
              />
              <InfoRow
                label="Post Office"
                value={toTitleCase(
                  employee.address_information.permanent_address.post
                )}
              />
              <InfoRow
                label="District"
                value={toTitleCase(
                  employee.address_information.permanent_address.district
                )}
              />
              <InfoRow
                label="State"
                value={toTitleCase(
                  employee.address_information.permanent_address.state
                )}
              />
              <InfoRow
                label="PIN Code"
                value={employee.address_information.permanent_address.pin_code}
              />
            </div>
          </InfoCard>

          {/* Contact Information */}
          <InfoCard title="Contact Information" icon={Phone}>
            <div className="space-y-1">
              <InfoRow
                label="Phone Number"
                value={toTitleCase(employee.contact_information.phone_number)}
              />
              <InfoRow
                label="Email"
                value={employee.contact_information.email}
              />
              <div className="pt-2">
                <h4 className="font-medium text-gray-900 mb-2">
                  Emergency Contact
                </h4>
                <InfoRow
                  label="Name"
                  value={toTitleCase(
                    employee.contact_information.emergency_contact.name
                  )}
                />
                <InfoRow
                  label="Mobile"
                  value={employee.contact_information.emergency_contact.mobile}
                />
                <InfoRow
                  label="Email"
                  value={employee.contact_information.emergency_contact.email}
                />
              </div>
            </div>
          </InfoCard>

          {/* Employment Details */}
          <InfoCard title="Employment Details" icon={Briefcase}>
            <div className="space-y-1">
              <InfoRow
                label="Designation"
                value={toTitleCase(employee.employment_details.designation)}
              />
              <InfoRow
                label="Grade"
                value={employee.employment_details.grade}
              />
              <InfoRow
                label="UAN Number"
                value={employee.employment_details.uan_number}
              />
              <InfoRow
                label="Date of Joining"
                value={formatDate(employee.employment_details.date_of_joining)}
              />
              <InfoRow
                label="Date of Appointment"
                value={formatDate(
                  employee.employment_details.date_of_appointment
                )}
              />
              <InfoRow
                label="Location"
                value={toTitleCase(employee.employment_details.location)}
              />
              <InfoRow
                label="Division"
                value={employee.employment_details.division}
              />
              <InfoRow
                label="Reporting Manager"
                value={toTitleCase(
                  employee.employment_details.reporting_manager
                )}
              />
              <InfoRow
                label="Source of Hire"
                value={toTitleCase(
                  employee.employment_details.source_of_candidate
                )}
              />
            </div>
          </InfoCard>

          {/* Salary and Compensation */}
          <InfoCard
            title="Salary & Compensation"
            icon={DollarSign}
            className="lg:col-span-2"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-lg font-semibold text-gray-900">
                  Annual CTC:{" "}
                  {formatCurrency(employee.salary_and_compensation.ctc_annual)}
                </span>
              </div>
              <button
                onClick={() => setShowSalaryDetails(!showSalaryDetails)}
                className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              >
                {showSalaryDetails ? (
                  <EyeOff className="h-4 w-4 mr-1" />
                ) : (
                  <Eye className="h-4 w-4 mr-1" />
                )}
                {showSalaryDetails ? "Hide Details" : "Show Details"}
              </button>
            </div>

            {showSalaryDetails && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <InfoRow
                    label="Basic Pay (Monthly)"
                    value={formatCurrency(
                      employee.salary_and_compensation.basic_pay.monthly
                    )}
                  />
                  <InfoRow
                    label="House Rent (Monthly)"
                    value={formatCurrency(
                      employee.salary_and_compensation.house_rent.monthly
                    )}
                  />
                  <InfoRow
                    label="Special Allowance (Monthly)"
                    value={formatCurrency(
                      employee.salary_and_compensation.special_allowance.monthly
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <InfoRow
                    label="Performance Allowance (Monthly)"
                    value={formatCurrency(
                      employee.salary_and_compensation.performance_allowance
                        .monthly
                    )}
                  />
                  <InfoRow
                    label="Bonus/Ex-gratia (Monthly)"
                    value={formatCurrency(
                      employee.salary_and_compensation.bonus_exgratia.monthly
                    )}
                  />
                  <InfoRow
                    label="PF Employer Contribution (Monthly)"
                    value={formatCurrency(
                      employee.salary_and_compensation.pf_employer_contribution
                        .monthly
                    )}
                  />
                </div>
              </div>
            )}
          </InfoCard>

          {/* Bank Details */}
          <InfoCard title="Bank Details" icon={CreditCard}>
            <div className="space-y-1">
              <InfoRow
                label="Bank Name"
                value={employee.bank_details.bank_name}
              />
              <InfoRow
                label="Account Number"
                value={employee.bank_details.account_number}
              />
              <InfoRow
                label="IFSC Code"
                value={employee.bank_details.ifsc_code}
              />
            </div>
          </InfoCard>

          {/* Identification Documents */}
          <InfoCard title="Identification Documents" icon={FileText}>
            <div className="space-y-1">
              <InfoRow
                label="Aadhaar Number"
                value={employee.identification_documents.aadhaar_number}
              />
              <InfoRow
                label="PAN Number"
                value={employee.identification_documents.pan_number}
              />
            </div>
          </InfoCard>

          {/* Nominee Information */}
          <InfoCard title="Nominee Information" icon={Users}>
            {employee.nominee_information.length > 0 ? (
              <div className="space-y-1">
                {employee.nominee_information.map((nominee, index) => (
                  <div
                    key={index}
                    className="py-2 border-b border-gray-100 last:border-b-0"
                  >
                    <InfoRow label="Name" value={nominee.name} />
                    <InfoRow
                      label="Relationship"
                      value={nominee.relationship}
                    />
                    <InfoRow label="Percentage" value={nominee.percentage} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                No nominee information provided
              </p>
            )}
          </InfoCard>

          {/* Education */}
          <InfoCard title="Education" icon={GraduationCap}>
            <div className="space-y-1">
              <InfoRow
                label="Higher Education"
                value={employee.education.higher_education}
              />
            </div>
          </InfoCard>

          {/* Declarations and Compliance */}
          <InfoCard
            title="Declarations & Compliance"
            icon={Shield}
            className="lg:col-span-2"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <InfoRow
                  label="Physical Disabilities"
                  value={
                    employee.declarations_and_compliance.physical_disabilities
                  }
                />
                <InfoRow
                  label="Major Illness"
                  value={employee.declarations_and_compliance.major_illness}
                />
              </div>
              <div className="space-y-1">
                <InfoRow
                  label="Court Cases"
                  value={employee.declarations_and_compliance.court_cases}
                />
                <InfoRow
                  label="Police Custody"
                  value={employee.declarations_and_compliance.police_custody}
                />
              </div>
            </div>
          </InfoCard>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;
